import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@nps.today/component-library';
import SidePanel from 'components/dialog/SidePanel';
import { ChildTenant, ChildTenantAccessUser, Claim } from '../../../@types/organization';
import {
  getChildTenantAccess,
  grantChildTenantAccess,
  removeChildTenantAccess
} from 'redux/slices/organization';
import { RootState, useSelector } from 'redux/store';
import useCustomDispatch from 'redux/dispatch';
import useAuth from 'hooks/useAuth';
import { useTranslator } from 'translation/useTranslator';
import { USER_ACCESS_TEMPLATE_ROLES } from 'utils/userAccessTemplates';
import ChildTenantAccessForm, { PickableUser } from './ChildTenantAccessForm';
import ChildTenantAccessRemove from './ChildTenantAccessRemove';

const WIDTH_FITTING_PERMISSIONS_MATRIX = 720;

type PanelView = 'list' | 'form' | 'remove';

const initials = (accessUser: ChildTenantAccessUser) =>
  (accessUser.name || accessUser.email)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

interface ChildTenantAccessPanelProps {
  childTenant: ChildTenant | null;
  onClose: () => void;
}

const ChildTenantAccessPanel: FunctionComponent<ChildTenantAccessPanelProps> = ({
  childTenant,
  onClose
}) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { user } = useAuth();
  const { childTenantAccessByTenantId } = useSelector((state: RootState) => state.organization);

  const [view, setView] = useState<PanelView>('list');
  const [selectedUsers, setSelectedUsers] = useState<PickableUser[]>([]);
  const [role, setRole] = useState<string>(USER_ACCESS_TEMPLATE_ROLES.campaignManager);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [showPermissions, setShowPermissions] = useState(false);

  const [userToRemove, setUserToRemove] = useState<ChildTenantAccessUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refresh = useCallback(() => {
    if (!childTenant) {
      return;
    }
    customDispatch({
      action: getChildTenantAccess,
      actionParameters: { childTenant },
      disableSuccessMessage: true
    });
  }, [childTenant, customDispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const showListForSelectedTenant = () => setView('list');
  useEffect(showListForSelectedTenant, [childTenant?.id]);

  const openAddUsersForm = () => {
    setSelectedUsers([]);
    setRole(USER_ACCESS_TEMPLATE_ROLES.campaignManager);
    setClaims([]);
    setShowPermissions(false);
    setView('form');
  };

  const openRemove = (accessUser: ChildTenantAccessUser) => {
    setUserToRemove(accessUser);
    setView('remove');
  };

  const backToList = () => {
    setView('list');
    setUserToRemove(null);
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    const granted: string[] = [];
    const recordGranted = (userId: string) => () => {
      granted.push(userId);
    };

    for (const target of selectedUsers) {
      await customDispatch({
        action: grantChildTenantAccess,
        actionParameters: { childTenant, userId: target.userId, role, claims },
        disableSuccessMessage: true,
        onSuccess: recordGranted(target.userId)
      });
    }

    setIsSubmitting(false);

    if (granted.length > 0) {
      refresh();
      backToList();
    }
  };

  const submitRemove = () => {
    if (!userToRemove) {
      return;
    }
    setIsSubmitting(true);
    customDispatch({
      action: removeChildTenantAccess,
      actionParameters: { childTenant, userId: userToRemove.userId },
      onSuccess: () => {
        refresh();
        backToList();
      },
      onFinally: () => {
        setIsSubmitting(false);
      }
    });
  };

  if (!childTenant) {
    return null;
  }

  const accessUsers = childTenantAccessByTenantId[childTenant.id];
  const isLoading = accessUsers === undefined;

  const title = () => {
    if (view === 'form') {
      return translator.addUsersToChildTenantTitle(childTenant.name);
    }
    if (view === 'remove' && userToRemove) {
      return translator.removeChildTenantAccessTitle(
        userToRemove.name || userToRemove.email,
        childTenant.name
      );
    }
    return childTenant.name;
  };

  const footer = () => {
    if (view === 'form') {
      return (
        <>
          <Button
            dataTestId="child-tenant-access-form-toggle-permissions"
            variant="text"
            sx={{ mr: 'auto' }}
            onClick={() => setShowPermissions((current) => !current)}
          >
            {showPermissions ? translator.hidePermissions() : translator.viewPermissions()}
          </Button>
          <Button
            dataTestId="child-tenant-access-form-cancel"
            variant="text"
            onClick={backToList}
            disabled={isSubmitting}
          >
            {translator.cancel()}
          </Button>
          <Button
            dataTestId="child-tenant-access-form-save"
            variant="contained"
            disabled={selectedUsers.length === 0 || !role}
            loading={isSubmitting}
            onClick={submitForm}
          >
            {translator.giveAccess()}
          </Button>
        </>
      );
    }

    if (view === 'remove') {
      return (
        <>
          <Button
            dataTestId="child-tenant-access-remove-cancel"
            variant="text"
            onClick={backToList}
            disabled={isSubmitting}
          >
            {translator.cancel()}
          </Button>
          <Button
            dataTestId="child-tenant-access-remove-confirm"
            variant="contained"
            color="error"
            loading={isSubmitting}
            onClick={submitRemove}
          >
            {translator.removeChildTenantAccess()}
          </Button>
        </>
      );
    }

    return undefined;
  };

  return (
    <SidePanel
      dataTestId="child-tenant-access-panel"
      open={Boolean(childTenant)}
      onClose={onClose}
      defaultWidth={WIDTH_FITTING_PERMISSIONS_MATRIX}
      title={
        view === 'list' ? (
          title()
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton
              data-testid="child-tenant-access-panel-back"
              size="small"
              onClick={backToList}
              aria-label={translator.back()}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            {title()}
          </Stack>
        )
      }
      subtitle={
        view === 'list' ? (
          <Typography variant="body2" color="text.secondary">
            {translator.childTenantAccessSubtitle()}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {childTenant.name}
          </Typography>
        )
      }
      footer={footer()}
    >
      {view === 'form' && (
        <ChildTenantAccessForm
          childTenant={childTenant}
          usersWithAccess={accessUsers ?? []}
          selectedUsers={selectedUsers}
          role={role}
          claims={claims}
          showPermissions={showPermissions}
          onSelectedUsersChange={setSelectedUsers}
          onRoleChange={(nextRole, nextClaims) => {
            setRole(nextRole);
            setClaims(nextClaims);
          }}
          onClaimsChange={setClaims}
        />
      )}

      {view === 'remove' && userToRemove && (
        <ChildTenantAccessRemove childTenant={childTenant} accessUser={userToRemove} />
      )}

      {view === 'list' && (
        <Stack spacing={2}>
          <Button
            dataTestId="child-tenant-access-panel-add"
            variant="contained"
            startIcon={<PersonAddAltIcon />}
            sx={{ alignSelf: 'flex-start' }}
            onClick={openAddUsersForm}
          >
            {translator.addUsers()}
          </Button>

          {isLoading && (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((row) => (
                <Stack key={row} direction="row" spacing={1.5} alignItems="center">
                  <Skeleton variant="circular" width={40} height={40} />
                  <Stack flexGrow={1}>
                    <Skeleton variant="text" width="45%" />
                    <Skeleton variant="text" width="65%" />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {!isLoading && accessUsers.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translator.noChildTenantAccessYet(childTenant.name)}
              </Typography>
            </Box>
          )}

          {!isLoading && accessUsers.length > 0 && (
            <Stack divider={<Divider />}>
              {accessUsers.map((accessUser) => {
                const isCurrentUser = accessUser.userId === user?.userId;
                return (
                  <Stack
                    key={accessUser.userId}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ py: 1.5 }}
                  >
                    <Avatar sx={{ width: 40, height: 40, fontSize: 13 }}>
                      {initials(accessUser)}
                    </Avatar>
                    <Stack flexGrow={1} minWidth={0}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="subtitle2" noWrap>
                          {accessUser.name || accessUser.email}
                        </Typography>
                        {isCurrentUser && (
                          <Chip size="small" color="primary" label={translator.you()} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {accessUser.email}
                      </Typography>
                    </Stack>
                    <Tooltip title={translator.removeFromAccount()}>
                      <IconButton
                        data-testid={`child-tenant-access-remove-${accessUser.userId}`}
                        size="small"
                        aria-label={translator.removeFromAccount()}
                        onClick={() => openRemove(accessUser)}
                      >
                        <DeleteOutlineIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </SidePanel>
  );
};

export default ChildTenantAccessPanel;
